import React, { useContext } from 'react';
import {
  Case,
  updateSubmitterInfo,
} from '../../../../services/buildfire/rdb/cases';
import styled from 'styled-components';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { dedup, uppercaseFirstLetter } from '../../../../util/string';
import {
  IonButton,
  IonInput,
  IonItem,
  IonLabel,
  IonNote,
  useIonToast,
} from '@ionic/react';
import Typography from '../../../common/Typography';
import classNames from 'classnames';
import Form from '../../../common/Form';
import IconButton from '../../../../mdc/IconButton';
import CaseContext from '../../../../services/buildfire/rdb/CaseContext';
import { useFirebase } from '../../../../services/firebase';
import { getDatabase } from 'firebase/database';
import { warning } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import useGoBackToCase from '../../../../hooks/useGoBackToCase';

interface EditSubmitterInfoProps {
  case: Case;
}

const Container = styled.div`
  padding: 16px;
  display: flex;
  justify-content: center;
`;
const StyledForm = styled(Form)`
  max-width: 600px;

  & > ion-input {
    margin-bottom: 16px;
  }
`;
const EmailItem = styled(IonItem)`
  --background: transparent;
  --padding-start: 0;
  --padding-end: 0;
  --inner-padding-end: 0;
`;
const ButtonContainer = styled.div`
  margin-top: 64px;
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const EditSubmitterInfo: React.FC<EditSubmitterInfoProps> = ({ case: c }) => {
  const [present] = useIonToast();
  const { app } = useFirebase();
  const { updateCase: updateCaseLocally } = useContext(CaseContext);
  const history = useHistory();
  const goBack = useGoBackToCase();

  return (
    <Container>
      <Formik
        initialValues={{
          submitterEmailAddresses: dedup([
            c.submitter.email || '',
            ...(c.submitter.additionalEmails || []),
          ]),
          submitterFirstName: c.submitter.first,
          submitterLastName: c.submitter.last,
        }}
        validationSchema={Yup.object({
          submitterEmailAddresses: Yup.array().of(
            Yup.string()
              .email('Invalid email address')
              .required('Cannot be empty')
          ),
          submitterFirstName: Yup.string().min(1).max(50).required('Required'),
          submitterLastName: Yup.string().min(1).max(50).required('Required'),
        })}
        validateOnMount
        onSubmit={async (values) => {
          const updatedCase: Case = {
            ...c,
            submitter: {
              ...c.submitter,
              first: values.submitterFirstName,
              last: values.submitterLastName,
              email: values.submitterEmailAddresses[0] || '',
              additionalEmails: values.submitterEmailAddresses.slice(1),
            },
          };
          const db = getDatabase(app);
          try {
            await updateSubmitterInfo(db, c.id, {
              first: updatedCase.submitter.first,
              last: updatedCase.submitter.last,
              email: updatedCase.submitter.email,
              additionalEmails: updatedCase.submitter.additionalEmails,
            });
            updateCaseLocally(updatedCase);
            goBack(c.id);
            present({
              message: 'Case updated successfully',
              duration: 4000,
            });
          } catch (e) {
            console.error(e);
            present({
              message: 'Error updating case info, please try again',
              duration: 5000,
              color: 'danger',
              icon: warning,
            });
            return;
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          setFieldTouched,
          isValid,
          isSubmitting,
        }) => (
          <StyledForm>
            <Typography
              as="h3"
              fontSize="20px"
              variant="secondary"
              margin="16px 0"
            >
              Edit Veterinarian Info
            </Typography>
            <IonInput
              fill="outline"
              value={values.submitterFirstName}
              className={classNames({
                ['ion-invalid']: errors.submitterFirstName,
                ['ion-touched']: touched.submitterFirstName,
              })}
              label="First Name"
              labelPlacement="floating"
              type="text"
              name="submitterFirstName"
              required
              onIonInput={(e) => {
                setFieldValue('submitterFirstName', e.detail.value);
                setFieldTouched('submitterFirstName', true);
              }}
              errorText={uppercaseFirstLetter(
                errors.submitterFirstName as string
              )}
            />
            <IonInput
              fill="outline"
              value={values.submitterLastName}
              className={classNames({
                ['ion-invalid']: errors.submitterLastName,
                ['ion-touched']: touched.submitterLastName,
              })}
              label="Last Name"
              labelPlacement="floating"
              type="text"
              name="submitterLastName"
              required
              onIonInput={(e) => {
                setFieldValue('submitterLastName', e.detail.value);
                setFieldTouched('submitterLastName', true);
              }}
              errorText={uppercaseFirstLetter(
                errors.submitterLastName as string
              )}
            />

            <Typography
              as="h4"
              fontSize="14px"
              variant="secondary"
              margin="16px 0 8px 0"
            >
              Email
            </Typography>
            <Typography fontSize="12px">
              An email report will be sent to the below email addresses when
              your case is signed off
            </Typography>
            {values.submitterEmailAddresses.map((email, i) => (
              <EmailItem key={i}>
                <IonLabel>
                  <IonInput
                    fill="outline"
                    value={email}
                    className={classNames({
                      ['ion-invalid']: errors.submitterEmailAddresses?.[i],
                      ['ion-touched']: touched.submitterEmailAddresses?.[i],
                    })}
                    type="email"
                    inputMode="email"
                    errorText={uppercaseFirstLetter(
                      errors.submitterEmailAddresses?.[i] as string
                    )}
                    name={`submitterEmailAddresses[${i}]`}
                    onIonInput={(e) => {
                      setFieldValue(
                        `submitterEmailAddresses[${i}]`,
                        e.detail.value
                      );
                      setFieldTouched(`submitterEmailAddresses[${i}]`, true);
                    }}
                  />
                </IonLabel>
                <IonNote slot="end">
                  <IconButton
                    type="button"
                    icon="delete"
                    onClick={() => {
                      const newEmails = values.submitterEmailAddresses.filter(
                        (e, j) => i !== j
                      );
                      setFieldTouched(`submitterEmailAddresses[${i}]`, false);
                      setFieldValue('submitterEmailAddresses', newEmails);
                    }}
                  />
                </IonNote>
              </EmailItem>
            ))}
            <IonButton
              expand="block"
              onClick={() => {
                const newEmails = values.submitterEmailAddresses.concat('');
                setFieldValue('submitterEmailAddresses', newEmails);
              }}
              type="button"
              fill="clear"
            >
              + Add email
            </IonButton>

            <ButtonContainer>
              <IonButton fill="clear" onClick={() => goBack(c.id)}>
                Back
              </IonButton>

              <IonButton
                type="submit"
                disabled={!isValid || isSubmitting}
                expand="block"
                style={{ minWidth: '120px' }}
              >
                Save
              </IonButton>
            </ButtonContainer>
          </StyledForm>
        )}
      </Formik>
    </Container>
  );
};

export default EditSubmitterInfo;
